import { useMemo, useState } from 'react';
import { Building2, FileText } from 'lucide-react';
import {
  DataGrid,
  EmptyState,
  Input,
  MetricCard,
  MetricCardGrid,
  Select,
} from '@aaimplatform/ui';
import type { ColumnDef } from '@aaimplatform/ui';
import { useAuthStore } from '../../stores/auth';
import type { components as UccLifecycleComponents } from '@aaimplatform/institutional-types/ucc-filing-lifecycle';
import { useAllFilings, useFilings, useFilingsRefresh } from '../../hooks/useUccFilings';
import {
  Absent,
  EnumBadge,
  FILING_STATUS,
  FILING_TYPE_LABELS,
  ListHeader,
  labelFor,
  tile,
  type ListReadState,
} from './CollateralListParts';

// Canonical types from the deployed ucc-filing lifecycle service
// (/api/v1/ucc-filing). The page was migrated 2026-06-19 off the dead
// contract-documentation stub (/api/ucc-filings) onto this service, mirroring
// the sibling lien/FilingStatus.tsx consumption pattern.
type UccFiling = UccLifecycleComponents['schemas']['UccFiling'];
type FilingStatusValue = UccLifecycleComponents['schemas']['FilingStatus'];
type FilingTypeValue = UccLifecycleComponents['schemas']['FilingType'];

// ---------------------------------------------------------------------------
// Reads (UI run r3, fi-g5-2 and fi-g5-3)
// ---------------------------------------------------------------------------

/**
 * One page size for both lists of filings in the Lien Filings group: this page
 * (Our Filings and Filings Awaiting Review) and Filing Status.
 */
const PAGE_SIZE = 20;

const FILING_STATUSES = Object.keys(FILING_STATUS) as FilingStatusValue[];
const FILING_TYPES = Object.keys(FILING_TYPE_LABELS) as FilingTypeValue[];

/** UccFiling.jurisdictionCode is a two-letter state code, matched exactly. */
const JURISDICTION_CODE = /^[A-Z]{2}$/;

/** Where a query stands, in the tile vocabulary. */
function readState(query: { isError: boolean; data?: unknown }): ListReadState {
  if (query.isError) return 'failed';
  if (query.data === undefined) return 'loading';
  return 'loaded';
}

function combineReads(...reads: ListReadState[]): ListReadState {
  if (reads.includes('failed')) return 'failed';
  if (reads.includes('loading')) return 'loading';
  return 'loaded';
}

/**
 * A server count of the institution's filings, in one status or in all. The
 * tiles read these instead of the length of one 100-row page filtered in the
 * browser, which undercounted every institution with more than 100 filings.
 */
function useFilingCount(tenantId: string, status?: FilingStatusValue) {
  const query = useFilings({ tenantId, status, page: 1, limit: 1 });
  return { read: readState(query), count: query.data?.count };
}

// ---------------------------------------------------------------------------
// UCCFilingsPage Component
// ---------------------------------------------------------------------------

/**
 * Optional configuration props. Sprint B Lane B2 (2026-05-17) added these so
 * the same page component can power both the canonical "Our Filings" surface
 * (/collateral/ucc-filings, every status) and the auto-populated "Filings
 * Awaiting Review" surface (/lien/awaiting-review, status seeded to draft).
 */
export interface UCCFilingsPageProps {
  /** Subtitle override; default 'Manage Uniform Commercial Code filings...'. */
  subtitleOverride?: string;
  /**
   * The FilingStatus the Status filter opens on, sent to the server with the
   * first read. The user can choose All statuses. Pass undefined for the
   * unfiltered list.
   */
  defaultStatusFilter?: FilingStatusValue;
  /**
   * The empty state shown when nothing matches `defaultStatusFilter` and the
   * user has changed neither the filters nor the search. The generic filter
   * hint ('Clear the search or a filter') would otherwise answer the page's own
   * seeded filter, which the user never set.
   */
  defaultFilterEmptyState?: { title: string; description: string };
}

const DEFAULT_SUBTITLE = 'Manage Uniform Commercial Code filings for collateral perfection';

/**
 * UCC Filings Page -- DataGrid over the deployed ucc-filing lifecycle service
 * (/api/v1/ucc-filing).
 *
 * Read-only list: server pagination, export, server-side status, type and
 * jurisdiction filters, and expiration tracking read from the whole active
 * set. Initial filings are auto-created from approved pledges. admin-portal has no filing detail view, so the page names none;
 * amendment, continuation and termination actions are not offered here.
 *
 * Filings belong to one institution. An FI user's session carries its
 * tenantId. An Aaim operator who has not chosen an institution in the
 * organization switcher carries none, and the page then asks for one and
 * sends no read: listFilings requires a uuid tenantId, and the empty value
 * the page used to send came back as an HTTP 400 banner (GH #2242).
 */
export function UCCFilingsPage(props: UCCFilingsPageProps = {}) {
  const tenantId = useAuthStore((s) => s.user?.tenantId);
  if (!tenantId) {
    return <SelectInstitution subtitle={props.subtitleOverride ?? DEFAULT_SUBTITLE} />;
  }
  return <UCCFilingsList {...props} tenantId={tenantId} />;
}

/** The page for a session with no institution: no tiles, no table, no read. */
function SelectInstitution({ subtitle }: { subtitle: string }) {
  return (
    <div>
      <div className="mb-6">
        <p className="text-sm font-sans text-tertiary">{subtitle}</p>
      </div>
      <EmptyState
        icon={<Building2 className="h-8 w-8" />}
        title="Select an institution"
        description="UCC filings belong to one institution. Choose an institution in the organization switcher to see its filings."
      />
    </div>
  );
}

/** The list for one institution, read with its tenantId. */
function UCCFilingsList({
  tenantId,
  subtitleOverride,
  defaultStatusFilter,
  defaultFilterEmptyState,
}: UCCFilingsPageProps & { tenantId: string }) {
  const [statusFilter, setStatusFilter] = useState<FilingStatusValue | ''>(
    defaultStatusFilter ?? '',
  );
  const [typeFilter, setTypeFilter] = useState<FilingTypeValue | ''>('');
  const [jurisdiction, setJurisdiction] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);

  const jurisdictionCode = JURISDICTION_CODE.test(jurisdiction) ? jurisdiction : undefined;
  const jurisdictionError =
    jurisdiction.length === 2 && !jurisdictionCode ? 'Enter a two-letter state code.' : undefined;

  // The table page. Status, type and jurisdiction filter on the server, so a
  // filing past the first page is still reachable (UI run r3, fi-g5-2).
  const tableQuery = useFilings(
    {
      tenantId,
      status: statusFilter || undefined,
      filingType: typeFilter || undefined,
      jurisdictionCode,
      page,
      limit: PAGE_SIZE,
    },
  );
  const { refresh, refreshing } = useFilingsRefresh();

  // The tiles count the institution's whole book, independent of the table
  // filter, from server counts.
  const total = useFilingCount(tenantId);
  const active = useFilingCount(tenantId, 'active');
  const draft = useFilingCount(tenantId, 'draft');
  const submitted = useFilingCount(tenantId, 'submitted');
  const activeFilings = useAllFilings({ tenantId, status: 'active' });

  const pendingRead = combineReads(draft.read, submitted.read);
  const pendingCount =
    draft.count != null && submitted.count != null ? draft.count + submitted.count : null;

  // The spec has no expiration filter, so the count comes from every active
  // filing. A read that stopped before the whole set is not a count.
  const expiringRead = readState(activeFilings);
  const expiringCount = useMemo(() => {
    const result = activeFilings.data;
    if (!result || !result.complete) return null;
    return result.filings.filter((f) => {
      if (!f.expirationDate) return false;
      const daysUntilExpiry = Math.ceil(
        (new Date(f.expirationDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24),
      );
      return daysUntilExpiry <= 90 && daysUntilExpiry > 0;
    }).length;
  }, [activeFilings.data]);
  const expiringIncomplete = activeFilings.data != null && !activeFilings.data.complete;

  // -------------------------------------------------------------------------
  // Rows on screen
  // -------------------------------------------------------------------------

  const pageRows: UccFiling[] = tableQuery.data?.data ?? [];
  // listFilings takes no search parameter, so the filing-number search reads
  // the page on screen, as it does on Filing Status.
  const rows = searchQuery.trim()
    ? pageRows.filter((f) =>
        (f.filingNumber ?? '').toLowerCase().includes(searchQuery.trim().toLowerCase()),
      )
    : pageRows;

  // -------------------------------------------------------------------------
  // DataGrid Column Definitions
  // -------------------------------------------------------------------------

  const columns: ColumnDef<UccFiling, unknown>[] = useMemo(() => [
    {
      accessorKey: 'filingNumber',
      header: 'Filing Number',
      // A filing with no number reads 'Not yet assigned', as on Filing Status.
      // It printed the first eight characters of the filing UUID, which read as
      // a jurisdiction-assigned number (UI run r3, fi-g5-3).
      cell: ({ row }) =>
        row.original.filingNumber ? (
          <span className="text-sm font-mono font-medium text-secondary leading-normal">
            {row.original.filingNumber}
          </span>
        ) : (
          <span className="text-sm font-sans text-tertiary leading-normal">Not yet assigned</span>
        ),
      size: 180,
    },
    {
      accessorKey: 'filingType',
      header: 'Type',
      cell: ({ getValue }) => (
        <span className="text-sm font-sans font-medium text-primary leading-normal">
          {labelFor(FILING_TYPE_LABELS, getValue() as FilingTypeValue)}
        </span>
      ),
      size: 150,
    },
    {
      accessorKey: 'jurisdictionCode',
      header: 'Jurisdiction',
      cell: ({ getValue }) => {
        const code = getValue() as string | null | undefined;
        return code ? (
          <span className="text-sm font-sans text-secondary leading-normal">{code}</span>
        ) : (
          <Absent />
        );
      },
      size: 120,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => (
        <EnumBadge value={getValue() as FilingStatusValue | null | undefined} map={FILING_STATUS} />
      ),
      size: 120,
    },
    {
      accessorKey: 'expirationDate',
      header: 'Expires',
      cell: ({ getValue }) => {
        const dateStr = getValue() as string | null | undefined;
        if (!dateStr) return <Absent />;
        return (
          <span className="text-sm font-mono text-secondary tabular-nums leading-normal">
            {new Date(dateStr).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}
          </span>
        );
      },
      size: 130,
    },
  ], []);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  // Tiles carry a status line only where the filings support one. There is
  // no "in good standing" claim: no field of a filing records standing, and
  // with no filings there is nothing to stand.
  const pendingTile = tile(pendingRead, pendingCount == null ? null : String(pendingCount));
  const expiringTile = tile(
    expiringRead,
    expiringCount == null ? null : String(expiringCount),
    expiringIncomplete ? 'Not counted: more active filings than one read covers' : undefined,
  );

  // The user has changed neither the search nor the filters: the only filter
  // applied is the one the page seeded. An empty list then answers the seeded
  // question, not a filter the user chose, so the filter hint does not apply.
  const onlySeededFilter =
    defaultStatusFilter != null &&
    statusFilter === defaultStatusFilter &&
    typeFilter === '' &&
    jurisdiction === '' &&
    searchQuery.trim() === '';
  const userFiltered =
    !onlySeededFilter &&
    (statusFilter !== '' || typeFilter !== '' || jurisdiction !== '' || searchQuery.trim() !== '');
  const emptyState: { title: string; description: string } = onlySeededFilter
    ? defaultFilterEmptyState ?? {
        title: `No ${FILING_STATUS[defaultStatusFilter].label.toLowerCase()} filings`,
        description: 'This page opens filtered to that status. Choose All statuses to see every filing.',
      }
    : userFiltered
      ? {
          title: 'No UCC filings match these filters',
          description: 'Clear the search or choose another status, filing type or jurisdiction.',
        }
      : {
          title: 'No UCC filings on file',
          description: 'Initial filings are created from approved pledges, and none has been created yet.',
        };

  return (
    <div>
      {/* The page title lives in the sticky topnav (AppHeader), which renders
          it as the page's h1 on every route that mounts this page,
          /lien/awaiting-review included (PortalLayout registers its title),
          so the body opens with the subtitle and draws no heading of its own. */}
      <ListHeader
        subtitle={
          <p className="text-sm font-sans text-tertiary">
            {subtitleOverride ?? DEFAULT_SUBTITLE}
          </p>
        }
        onRefresh={() => void refresh()}
        refreshing={refreshing}
      />

      <MetricCardGrid className="mb-6">
        <MetricCard
          label="Total Filings"
          {...tile(total.read, total.count == null ? null : String(total.count))}
        />
        <MetricCard
          label="Active Filings"
          {...tile(active.read, active.count == null ? null : String(active.count))}
        />
        <MetricCard
          label="Pending"
          {...pendingTile}
          change={pendingRead === 'loaded' && (pendingCount ?? 0) > 0 ? 'Awaiting action' : undefined}
          changeType="neutral"
        />
        <MetricCard
          label="Expiring (90 days)"
          {...expiringTile}
          change={
            expiringCount == null || !active.count
              ? undefined
              : expiringCount > 0
                ? 'Renewal needed'
                : 'None expiring'
          }
          changeType={(expiringCount ?? 0) > 0 ? 'negative' : 'positive'}
        />
      </MetricCardGrid>

      {/* The same labeled inputs as Filing Status, so the two lists of one
          nav group filter the same way (UI run r3, fi-g5-3). */}
      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Input
          id="ucc-filings-search"
          label="Filing number"
          placeholder="Search by filing number"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <Select
          id="ucc-filings-status"
          label="Status"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as FilingStatusValue | '');
            setPage(1);
          }}
        >
          <option value="">All statuses</option>
          {FILING_STATUSES.map((s) => (
            <option key={s} value={s}>
              {FILING_STATUS[s].label}
            </option>
          ))}
        </Select>
        <Select
          id="ucc-filings-type"
          label="Filing type"
          value={typeFilter}
          onChange={(e) => {
            setTypeFilter(e.target.value as FilingTypeValue | '');
            setPage(1);
          }}
        >
          <option value="">All types</option>
          {FILING_TYPES.map((t) => (
            <option key={t} value={t}>
              {FILING_TYPE_LABELS[t]}
            </option>
          ))}
        </Select>
        <Input
          id="ucc-filings-jurisdiction"
          label="Jurisdiction"
          placeholder="Two-letter state code"
          maxLength={2}
          value={jurisdiction}
          onChange={(e) => {
            setJurisdiction(e.target.value.toUpperCase());
            setPage(1);
          }}
          error={jurisdictionError}
        />
      </div>

      <DataGrid<UccFiling>
        data={rows}
        columns={columns}
        isLoading={tableQuery.isLoading}
        errorMessage={
          tableQuery.isError
            ? `UCC filings could not be read: ${tableQuery.error.message}`
            : undefined
        }
        emptyState={
          <EmptyState
            icon={<FileText className="h-8 w-8" />}
            title={emptyState.title}
            description={emptyState.description}
          />
        }
        pagination={{
          page: page - 1,
          pageSize: PAGE_SIZE,
          total: tableQuery.data?.count ?? 0,
          onPageChange: (next) => setPage(next + 1),
        }}
        enableExport
        enableColumnResize
        hoverable
        striped
        getRowId={(row) => row.filingId ?? row.filingNumber ?? ''}
      />
    </div>
  );
}
